import React, { createContext, useState, useContext, useCallback, useEffect, useMemo, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import api from '../services/api';

const CartContext = createContext();

const GUEST_WISHLIST_KEY = 'guestWishlist';
const LEGACY_GUEST_WISHLIST_KEY = 'guest_wishlist_v1';

function safeParse(json, fallback) {
  try {
    return JSON.parse(json);
  } catch {
    return fallback;
  }
}

function getGuestWishlistItems() {
  const legacy = safeParse(localStorage.getItem(LEGACY_GUEST_WISHLIST_KEY), null);
  if (legacy && Array.isArray(legacy.items)) {
    return legacy.items;
  }
  const raw = safeParse(localStorage.getItem(GUEST_WISHLIST_KEY), []);
  return Array.isArray(raw) ? raw : [];
}

function setGuestWishlistItems(items) {
  localStorage.setItem(GUEST_WISHLIST_KEY, JSON.stringify(items));
  localStorage.removeItem(LEGACY_GUEST_WISHLIST_KEY);
}

function removeFromGuestWishlist(productId) {
  const pid = productId?.toString?.() ?? String(productId);
  const items = getGuestWishlistItems().filter((it) => (it.productId ?? it._id ?? it.id)?.toString?.() !== pid);
  setGuestWishlistItems(items);
  return items;
}

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const queryClient = useQueryClient();
  const [actionLock, setActionLock] = useState(false);
  const prevAuthRef = useRef(isAuthenticated);

  const cartQueryKey = useMemo(
    () => ['cart', isAuthenticated, String(user?._id ?? 'guest')],
    [isAuthenticated, user?._id]
  );

  const {
    data: cart = { items: [], total: 0, count: 0 },
    isFetching: cartFetching,
  } = useQuery({
    queryKey: cartQueryKey,
    queryFn: async () => (await api.get('cart')).data.cart,
    staleTime: 60 * 1000,
  });

  const setCartCache = useCallback(
    (next) => {
      queryClient.setQueryData(cartQueryKey, next);
    },
    [queryClient, cartQueryKey]
  );

  const invalidateCart = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['cart'] });
  }, [queryClient]);

  useEffect(() => {
    const handler = (event) => {
      const nextCart = event?.detail?.cart;
      if (nextCart) setCartCache(nextCart);
    };

    window.addEventListener('ecomm:wishlist-sync', handler);
    return () => window.removeEventListener('ecomm:wishlist-sync', handler);
  }, [setCartCache]);

  // On logout (auth -> guest), clear cart immediately and rotate guest session id.
  // This prevents a just-logged-out user from seeing the previous cart contents.
  useEffect(() => {
    const wasAuthed = Boolean(prevAuthRef.current);
    const isAuthedNow = Boolean(isAuthenticated);
    prevAuthRef.current = isAuthedNow;

    if (wasAuthed && !isAuthedNow) {
      try {
        localStorage.removeItem('cart_session_id');
      } catch {
        // ignore
      }

      const empty = { items: [], total: 0, count: 0 };
      queryClient.setQueryData(cartQueryKey, empty);
      queryClient.removeQueries({ queryKey: ['cart'] });
      window.dispatchEvent(new CustomEvent('ecomm:cart-sync', { detail: { cart: empty } }));
    }
  }, [isAuthenticated, queryClient, cartQueryKey]);

  const loading = cartFetching || actionLock;

  const addToCart = useCallback(
    async (productId, quantity = 1, productSnapshot = null) => {
      if (actionLock) return;

      setActionLock(true);

      try {
        const response = await api.post('cart/add', { productId, quantity });
        setCartCache(response.data.cart);

        if (!isAuthenticated) {
          const nextGuestWishlist = removeFromGuestWishlist(productId);
          window.dispatchEvent(
            new CustomEvent('ecomm:cart-sync', {
              detail: {
                cart: response.data.cart,
                wishlist: { items: nextGuestWishlist, count: nextGuestWishlist.length },
              },
            })
          );
        } else if (response.data.wishlist) {
          window.dispatchEvent(
            new CustomEvent('ecomm:cart-sync', {
              detail: { cart: response.data.cart, wishlist: response.data.wishlist },
            })
          );
        }

        return { success: true };
      } catch (error) {
        return {
          success: false,
          message: error.response?.data?.message || 'Failed to add to cart',
        };
      } finally {
        setTimeout(() => setActionLock(false), 300);
      }
    },
    [isAuthenticated, actionLock, setCartCache]
  );

  const removeFromCart = useCallback(
    async (productId) => {
      if (actionLock) return;

      setActionLock(true);

      try {
        const response = await api.delete(`cart/remove/${productId}`);
        setCartCache(response.data.cart);

        if (response.data.wishlist) {
          window.dispatchEvent(
            new CustomEvent('ecomm:cart-sync', {
              detail: { cart: response.data.cart, wishlist: response.data.wishlist },
            })
          );
        }

        return { success: true };
      } catch (error) {
        invalidateCart();
        return {
          success: false,
          message: error.response?.data?.message || 'Failed to remove from cart',
        };
      } finally {
        setTimeout(() => setActionLock(false), 300);
      }
    },
    [actionLock, setCartCache, invalidateCart]
  );

  const updateQuantity = useCallback(
    async (productId, quantity) => {
      if (actionLock) return;
      if (quantity < 1) {
        await removeFromCart(productId);
        return;
      }

      setActionLock(true);

      try {
        const response = await api.patch('cart/update', { productId, quantity });
        setCartCache(response.data.cart);

        if (response.data.wishlist) {
          window.dispatchEvent(
            new CustomEvent('ecomm:cart-sync', {
              detail: { cart: response.data.cart, wishlist: response.data.wishlist },
            })
          );
        }

        return { success: true };
      } catch (error) {
        invalidateCart();
        return {
          success: false,
          message: error.response?.data?.message || 'Failed to update cart',
        };
      } finally {
        setTimeout(() => setActionLock(false), 300);
      }
    },
    [actionLock, removeFromCart, setCartCache, invalidateCart]
  );

  const moveCartToWishlist = useCallback(
    async (productId) => {
      if (actionLock) return;

      setActionLock(true);

      try {
        if (!isAuthenticated) {
          const pid = productId?.toString?.() ?? String(productId);
          const existing =
            cart.items.find((it) => it.productId?.toString?.() === pid || it.productId === pid) || null;

          const response = await api.delete(`cart/remove/${productId}`);
          setCartCache(response.data.cart);

          const currentWishlist = getGuestWishlistItems();
          const already = currentWishlist.some((it) => (it.productId ?? it._id ?? it.id)?.toString?.() === pid);
          if (!already) {
            const unitPrice = Number(existing?.unitPrice ?? existing?.price ?? 0);
            const item = {
              productId: pid,
              name: existing?.name,
              image: existing?.image,
              unitPrice,
              price: unitPrice,
              stockStatus: existing?.stockStatus ?? null,
              addedAt: new Date().toISOString(),
            };
            const nextWishlist = [item, ...currentWishlist].filter(Boolean);
            setGuestWishlistItems(nextWishlist);

            window.dispatchEvent(
              new CustomEvent('ecomm:cart-sync', {
                detail: { cart: response.data.cart, wishlist: { items: nextWishlist, count: nextWishlist.length } },
              })
            );
          } else {
            window.dispatchEvent(
              new CustomEvent('ecomm:cart-sync', {
                detail: {
                  cart: response.data.cart,
                  wishlist: { items: currentWishlist, count: currentWishlist.length },
                },
              })
            );
          }

          return { success: true };
        }

        const response = await api.post('cart/move-to-wishlist', { productId });
        setCartCache(response.data.cart);
        window.dispatchEvent(
          new CustomEvent('ecomm:cart-sync', {
            detail: {
              cart: response.data.cart,
              wishlist: response.data.wishlist,
            },
          })
        );

        return { success: true };
      } catch (error) {
        return { success: false, message: error.response?.data?.message || 'Failed to move item' };
      } finally {
        setTimeout(() => setActionLock(false), 300);
      }
    },
    [actionLock, isAuthenticated, cart.items, setCartCache]
  );

  const getCartQuantity = useCallback(
    (productId) => {
      const item = cart.items.find(
        (item) => item.productId?.toString() === productId?.toString() || item.productId === productId
      );
      return item ? item.quantity : 0;
    },
    [cart.items]
  );

  const isInCart = useCallback(
    (productId) => {
      return cart.items.some(
        (item) => item.productId?.toString() === productId?.toString() || item.productId === productId
      );
    },
    [cart.items]
  );

  const fetchCart = useCallback(() => invalidateCart(), [invalidateCart]);

  const value = {
    cart,
    loading,
    addToCart,
    updateQuantity,
    removeFromCart,
    moveCartToWishlist,
    getCartQuantity,
    isInCart,
    fetchCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
